import { useMemo } from 'react';
import Link from 'next/link';
import { Typography } from '@/primitives';
import { Card, CardContent, CardMedia } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { lineClampSx } from '@/utils/text';
import { slate } from '@/lib/colours';

export type NewsCardProps = {
  date?: Date;
  imgUrl?: string;
  isBrief?: boolean;
  /** Dark landing section styling */
  landing?: boolean;
  referenceLink: string;
  snippet: string;
  title: string;
};

export default function NewsCard({
  date,
  imgUrl,
  isBrief = false,
  landing = false,
  referenceLink,
  snippet,
  title,
}: NewsCardProps) {
  const timeAgo = date ? formatDistanceToNow(date, { addSuffix: true }) : '';
  const formattedTime = useMemo(
    () => timeAgo.replace(/^about |^over |^less than |^almost /, ''),
    [timeAgo],
  );

  const titleColor = landing ? slate[100] : slate[900];
  const snippetColor = landing ? slate[400] : slate[600];
  const timeColor = landing ? slate[500] : slate[500];

  return (
    <Link href={referenceLink} style={{ textDecoration: 'none' }}>
      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: isBrief ? 'row' : 'column',
          borderRadius: landing ? '14px' : 0,
          maxWidth: { xs: 'unset', md: '378px' },
          backgroundColor: landing ? slate[900] : 'transparent',
          border: landing ? `1px solid ${slate[800]}` : 'none',
          overflow: 'hidden',
          transition: landing
            ? 'border-color 0.2s, box-shadow 0.2s'
            : undefined,
          '&:hover': landing
            ? {
                borderColor: slate[600],
                boxShadow: `0 12px 40px -20px rgba(0,0,0,0.45)`,
              }
            : undefined,
        }}
      >
        <CardMedia
          alt="news image"
          component="img"
          image={imgUrl}
          sx={{
            borderRadius: landing ? 0 : isBrief ? 0 : '12px',
            height: {
              sm: isBrief ? '96px' : 'unset',
            },
            width: {
              sm: isBrief ? '96px' : '100%',
            },
            aspectRatio: isBrief
              ? '1 / 1'
              : {
                  xs: '350 / 300',
                  md: '336 / 260',
                },
          }}
        />
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: isBrief
              ? landing
                ? '16px 20px !important'
                : '0 16px !important'
              : landing
                ? '20px !important'
                : '12px 0 !important',
          }}
        >
          <Typography
            color={titleColor}
            variant={isBrief ? 'titlesmall' : 'titlemedium'}
          >
            {title}
          </Typography>
          <Typography
            color={snippetColor}
            sx={{
              ...lineClampSx,
              mt: isBrief ? 0 : '4px',
              lineHeight: isBrief ? '24px' : '20px',
            }}
            variant="bodymedium"
          >
            {snippet}
          </Typography>
          <Typography
            color={timeColor}
            sx={{ mt: isBrief ? 0 : '8px' }}
            variant="bodymedium"
          >
            {formattedTime}
          </Typography>
        </CardContent>
      </Card>
    </Link>
  );
}
